/**
 * RSS parsing and fetching into the store news model.
 */
import { XMLParser } from "fast-xml-parser";

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: "@_",
  htmlEntities: true,
  isArray: (tagName) => tagName === "item" || tagName === "media:content",
});

let retryAfter = 0;

function asArray(value) {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : [value];
}

function asText(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function firstImageFromHtml(html) {
  const match = /<img[^>]+src=["']([^"']+)["']/i.exec(asText(html));
  return match ? match[1] : "";
}

function pickLargestMediaContent(item) {
  const media = asArray(item["media:content"]);
  let best = "";
  let bestWidth = -1;

  for (const entry of media) {
    const url = typeof entry === "string" ? entry : entry?.["@_url"];
    if (!url) continue;
    const width = Number(entry?.["@_width"]);
    if (Number.isFinite(width) ? width > bestWidth : best === "") {
      best = url;
      bestWidth = Number.isFinite(width) ? width : bestWidth;
    }
  }

  return best;
}

function extractImage(item) {
  return decodeEntities(asText(
    pickLargestMediaContent(item) ||
    (asText(item.enclosure?.["@_type"]).startsWith("image")
      ? asText(item.enclosure?.["@_url"])
      : "") ||
    firstImageFromHtml(item["content:encoded"]) ||
    firstImageFromHtml(item.description) ||
    ""
  ));
}

const PT_MONTHS = {
  jan: 0, fev: 1, mar: 2, abr: 3, mai: 4, jun: 5,
  jul: 6, ago: 7, set: 8, out: 9, nov: 10, dez: 11,
};

function parsePtBrDate(text) {
  const match = /^\w{3},\s+(\d{1,2})\s+(\w{3})\s+(\d{4})\s+(\d{2}):(\d{2}):(\d{2})\s+([+-]\d{4})$/.exec(
    text.trim()
  );
  if (!match) return NaN;
  const month = PT_MONTHS[match[2].toLowerCase()];
  if (month === undefined) return NaN;
  const pad = (n) => String(n).padStart(2, "0");
  const offset = match[7];
  return Date.parse(
    `${match[3]}-${pad(month + 1)}-${pad(match[1])}T${match[4]}:${match[5]}:${match[6]}${offset.slice(0, 3)}:${offset.slice(3)}`
  );
}

const SNIPPET_MAX_LENGTH = 180;

function stripHtml(html) {
  return asText(html)
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const NAMED_ENTITIES = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " };

function decodeEntities(text) {
  return text
    .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/&(amp|lt|gt|quot|apos|nbsp);/g, (_, name) => NAMED_ENTITIES[name]);
}

function truncateSnippet(text) {
  if (text.length <= SNIPPET_MAX_LENGTH) return text;
  const cut = text.lastIndexOf(" ", SNIPPET_MAX_LENGTH);
  return `${text.slice(0, cut > 0 ? cut : SNIPPET_MAX_LENGTH)}…`;
}

function extractSnippet(item) {
  const text = decodeEntities(
    stripHtml(item.description) || stripHtml(item["content:encoded"])
  );
  return text ? truncateSnippet(text) : "";
}

function extractPublishedAt(item) {
  const text = asText(item.pubDate);
  const parsed = Date.parse(text);
  if (Number.isFinite(parsed)) return parsed;
  return parsePtBrDate(text);
}

function toNews(item, source) {
  const url = asText(item.link) || asText(item.guid);
  const title = asText(item.title);
  const publishedAt = extractPublishedAt(item);
  if (!url || !title || !Number.isFinite(publishedAt)) return null;

  return {
    url,
    title,
    image: extractImage(item),
    source,
    publishedAt,
    snippet: extractSnippet(item),
  };
}

export function extractNewsFromRss(xml, { source = "" } = {}) {
  if (typeof xml !== "string" || xml.trim() === "") return [];

  let parsed;
  try {
    parsed = parser.parse(xml);
  } catch {
    return [];
  }

  const channel = parsed?.rss?.channel;
  if (!channel) return [];

  const resolvedSource = source || asText(channel.title);
  const items = channel.item === undefined ? [] : asArray(channel.item);

  return items
    .map((item) => toNews(item ?? {}, resolvedSource))
    .filter((news) => news !== null);
}

async function readBodyText(response) {
  const buffer = await response.arrayBuffer();
  const headerCharset = /charset\s*=\s*["']?([^;\s"']+)/i.exec(response.headers.get("content-type") || "")?.[1];
  const prefix = new TextDecoder("ascii").decode(buffer.slice(0, 256));
  const xmlCharset = /^\s*<\?xml\b[^>]*encoding\s*=\s*["']([^"']+)["']/i.exec(prefix)?.[1];
  try {
    return new TextDecoder(headerCharset ?? xmlCharset ?? "utf-8").decode(buffer);
  } catch {
    return new TextDecoder("utf-8").decode(buffer);
  }
}

export async function fetchRssNews(feedUrl, { source = "", proxy = "" } = {}) {
  if (proxy && Date.now() < retryAfter) throw new Error("RSS API temporarily paused");
  const target = proxy ? `${proxy}${encodeURIComponent(feedUrl)}` : feedUrl;
  const response = await fetch(target);
  if (proxy && (response.status === 429 || response.status === 503)) {
    const header = response.headers.get("Retry-After");
    const seconds = Number(header);
    const deadline = header && Number.isFinite(seconds)
      ? Date.now() + Math.max(1, seconds) * 1000
      : Date.parse(header ?? "");
    retryAfter = Math.max(retryAfter, Number.isFinite(deadline) ? deadline : Date.now() + 60000);
  }
  if (!response.ok) {
    throw new Error(`RSS fetch failed: ${response.status} ${response.statusText}`);
  }
  return extractNewsFromRss(await readBodyText(response), { source });
}
